// src/pages/EntityPage.js
import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Button, 
  CircularProgress, 
  Box, 
  List, 
  ListItem, 
  TextField, 
  Paper, 
  IconButton 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const EntityPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!location.state || !location.state.projectID) {
      navigate('/projects');
    }
  }, [location.state, navigate]);

  const {
    updatedCategoryList = [],
    projectID,
    codeName,
    dataCount,
    question,
    remainingQuota,
    selectedTags = []
  } = location.state || {};

  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (updatedCategoryList && updatedCategoryList.length > 0) {
      const transformed = updatedCategoryList.map(item => ({
        categoryName: item.category_name || item.categoryName || '',
        categoryCode: item.category_code || item.categoryCode || '',
      }));
      setCategories(transformed);
    }
  }, [updatedCategoryList]);

  const addCategory = () => {
    setCategories(prev => [...prev, { categoryName: '', categoryCode: '' }]);
  };

  const deleteCategory = (index) => {
    setCategories(prev => prev.filter((_, i) => i !== index));
  };

  const updateCategoryName = (index, value) => {
    setCategories(prev => {
      const newCategories = [...prev];
      newCategories[index].categoryName = value;
      return newCategories;
    });
  };

  const saveCategoriesHandler = () => {
    setSaving(true);
    let maxCode = 0;
    categories.forEach(cat => {
      const codeNum = parseInt(cat.categoryCode, 10);
      if (!isNaN(codeNum)) {
        maxCode = Math.max(maxCode, codeNum);
      }
    });
    const updatedList = categories.map(cat => {
      let { categoryName, categoryCode } = cat;
      if (!categoryCode) {
        maxCode += 1;
        categoryCode = maxCode.toString();
      }
      return { categoryName, categoryCode };
    });
    if (updatedList.length > 200) {
      alert(t('codebook.exceedCategories') || 'The codebook should not exceed 200 categories.');
      setSaving(false);
      return;
    }
    if (Number(dataCount) > Number(remainingQuota)) {
      alert(t('codebook.quotaExceeded') || "Quota exceeded!");
      setSaving(false);
      navigate(-1);
      return;
    }
    const formData = new FormData();
    formData.append("projectID", projectID);
    formData.append("userEmail", currentUser.email);
    formData.append("updatedCategoryList", JSON.stringify(updatedList));
    formData.append("tags", JSON.stringify(selectedTags));
    formData.append("codeName", codeName);
    fetch("https://codini-project-2-brand-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData
    })
      .then(response => response.json())
      .catch(error => {
        console.error(t('entityPage.sendError') || 'Error sending entity categories:', error);
      })
      .finally(() => {
        setTimeout(() => {
          setSaving(false);
          navigate(-1);
        }, 7000);
      });
  };

  return (
    <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', padding: '20px' }}>
      <Container sx={{ mt: 1, backgroundColor: '#fff', borderRadius: 2, padding: '3px' }}>
        {/* Üst Başlık ve Proje Bilgileri */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, mb: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/ProjectsPage')}>
            {t('cancel') || 'İptal'}
          </Button>
        </Box>
        <Paper elevation={1} sx={{ p: 1, mb: 2, background:'#fed9b7' }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {t('entityPage.brandBook') || "Brand Book"}
          </Typography>
          <Typography variant="body1">
            {t('question') || "Question"}: {question}
          </Typography>
          <Typography variant="body1">
            {t('dataCount') || "Data Count"}: {dataCount}
          </Typography>
        </Paper>

        {/* Kategori Listesi */}
        <List sx={{ width: '100%', mb: 1 }}>
          {categories.length > 0 ? (
            categories.map((cat, index) => (
              <ListItem
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 0.5,
                  borderBottom: '1px solid #eee',
                  py: 0.5,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 60, mr: 0.5 }}>
                  {cat.categoryCode || ' '}
                </Typography>
                <TextField
                  value={cat.categoryName}
                  placeholder={t('codebook.category') || "Category"}
                  onChange={(e) => updateCategoryName(index, e.target.value)}
                  size="small"
                  sx={{ flexGrow: 1, mr: 0.5 }}
                />
                <IconButton
                  size="small"
                  onClick={() => deleteCategory(index)}
                  aria-label={t('delete') || 'Delete'}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))
          ) : (
            <Typography variant="body1" align="center">
              {t('codebook.noCategory') || "Kategori bulunamadı."}
            </Typography>
          )}
        </List>

        {/* Kategori Ekle Butonu */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={addCategory}>
            {t('codebook.addCategory') || "Kategori Ekle"}
          </Button>
        </Box>

        {/* Kaydet / İptal Butonları */}
        <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="contained" color="success" onClick={saveCategoriesHandler}>
            {t('codebook.startCoding') || "Kodlamaya Başla"}
          </Button>
        </Box>

        {saving && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default EntityPage;